<template>
  <div class="p-fileupload p-fileupload-advanced p-component" v-if="isAdvanced">
    <div class="file-selecter">
      <div class="p-fileupload-buttonbar">
        <span
          :class="advancedChooseButtonClass"
          @click="choose"
          @keydown.enter="choose"
          @focus="onFocus"
          @blur="onBlur"
          v-ripple
          tabindex="0"
        >
          <input
            ref="fileInput"
            type="file"
            @change="onFileSelect"
            :multiple="multiple"
            :accept="accept"
            :disabled="chooseDisabled"
          />
          <span class="p-button-icon p-button-icon-left pi pi-fw pi-plus"></span>
          <span class="p-button-label">{{ chooseButtonLabel }}</span>
        </span>
        <FileUploadButton
          :label="uploadButtonLabel"
          icon="pi pi-upload"
          @click="upload"
          :disabled="uploadDisabled"
          v-if="showUploadButton"
        />
        <FileUploadButton
          :label="cancelButtonLabel"
          icon="pi pi-times"
          @click="clear"
          :disabled="cancelDisabled"
          v-if="showCancelButton"
        />
      </div>
      <div
        ref="content"
        class="p-fileupload-content"
        @dragenter="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <div class="p-fileupload-empty" v-if="$slots.empty">
          <slot name="empty"></slot>
          <slot name="dropmessage"></slot>
        </div>
      </div>
    </div>
    <div class="progress">
      <FileUploadProgressBar :value="progress" v-if="hasFiles" />
      <FileUploadMessage v-for="msg of messages" severity="error" :key="msg">{{
        msg
      }}</FileUploadMessage>
    </div>
    <div class="p-fileupload-files" v-if="hasFiles">
      <div
        class="p-fileupload-row"
        v-for="(file, index) of files"
        :key="file.name + file.type + file.size"
      >
        <div>
          <img role="presentation" :alt="file.name" :src="file.objectURL" :width="previewWidth" />
        </div>
        <div class="file-info">
          <span class="-spnone file-info__name">{{ file.name }}</span>
          <span class="file-size">{{ formatSize(file.size) }}</span>
        </div>
        <div class="file-remove">
          <FileUploadButton type="button" icon="pi pi-times" @click="remove(index)" />
        </div>
      </div>
    </div>
  </div>
  <div class="p-fileupload p-fileupload-basic p-component" v-else-if="isBasic">
    <FileUploadMessage v-for="msg of messages" severity="error" :key="msg">{{
      msg
    }}</FileUploadMessage>
    <span
      :class="basicChooseButtonClass"
      @mouseup="onBasicUploaderClick"
      @keydown.enter="choose"
      @focus="onFocus"
      @blur="onBlur"
      v-ripple
      tabindex="0"
    >
      <span :class="basicChooseButtonIconClass"></span>
      <span class="p-button-label">{{ basicChooseButtonLabel }}</span>
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        :disabled="disabled"
        @change="onFileSelect"
        @focus="onFocus"
        @blur="onBlur"
        v-if="!hasFiles"
      />
    </span>
  </div>
</template>

<script>
/**
 * withCredentials 추가
 */
import Button from "primevue/button";
import ProgressBar from "primevue/progressbar";
import Message from "primevue/message";
import { DomHandler } from "primevue/utils";
import Ripple from "primevue/ripple";
import { useStore } from "@/store";
import UrlDataReader from "@/utils/UrlDataReader";

export default {
  emits: [
    "select",
    "uploader",
    "before-upload",
    "progress",
    "upload",
    "error",
    "before-send",
    "clear",
  ],
  name: "File Upload",
  props: {
    name: {
      type: String,
      default: null,
    },
    url: {
      type: String,
      default: null,
    },
    mode: {
      type: String,
      default: "advanced",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    auto: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: null,
    },
    invalidFileSizeMessage: {
      type: String,
      default: "{0}: Invalid file size, file size should be smaller than {1}.",
    },
    fileLimit: {
      type: Number,
      default: null,
    },
    invalidFileLimitMessage: {
      type: String,
      default: "Maximum number of files exceeded, limit is {0} at most.",
    },
    withCredentials: {
      type: Boolean,
      default: false,
    },
    previewWidth: {
      type: Number,
      default: 50,
    },
    chooseLabel: {
      type: String,
      default: null,
    },
    uploadLabel: {
      type: String,
      default: null,
    },
    cancelLabel: {
      type: String,
      default: null,
    },
    customUpload: {
      type: Boolean,
      default: false,
    },
    showUploadButton: {
      type: Boolean,
      default: true,
    },
    showCancelButton: {
      type: Boolean,
      default: true,
    },
  },
  duplicateIEEvent: false,
  data() {
    return {
      uploadedFileCount: 0,
      files: [],
      messages: [],
      focused: false,
      progress: null,
    };
  },
  methods: {
    getFilesCount() {
      return this.files.length;
    },

    async onFileSelect(event) {
      if (event.type !== "drop" && this.isIE11() && this.duplicateIEEvent) {
        this.duplicateIEEvent = false;
        return;
      }

      const accepts = this.accept.split(",");
      console.log("onFileSelect.accept", accepts);

      this.messages = [];
      this.files = this.files || [];
      let files = event.dataTransfer ? event.dataTransfer.files : event.target.files;
      console.log("onFileSelect", files);
      for (let file of files) {
        console.log("file.name", file.name);
        const matched = accepts.filter((v) => {
          return file.name.indexOf(v) > 0;
        });
        console.log("matched", matched);
        if (matched.length <= 0) continue;

        if (!this.isFileSelected(file)) {
          if (this.validate(file)) {
            if (this.isImage(file)) {
              file.objectURL = window.URL.createObjectURL(file);
            } else {
              file.objectURL = "/images/new/file.svg";
              console.log("not image file=>" + file.name);
              file.objectURL = await UrlDataReader.toDataURL("/images/new/file.svg");
              console.log(file.objectURL, "file.objectURL");
            }
          }
          this.files.push(file);
        }
      }

      this.$emit("select", { originalEvent: event, files: files });

      if (this.fileLimit) {
        this.checkFileLimit();
      }

      if (this.auto && this.hasFiles && !this.isFileLimitExceeded()) {
        this.upload();
      }

      if (event.type !== "drop" && this.isIE11()) {
        this.clearIEInput();
      } else {
        this.clearInputElement();
      }
    },
    choose() {
      this.$refs.fileInput.click();
    },
    async upload(url) {
      const store = useStore();
      if (!url) url = this.url;
      if (this.customUpload) {
        if (this.fileLimit) {
          this.uploadedFileCount += this.files.length;
        }

        this.$emit("uploader", { files: this.files });
      } else {
        let xhr = new XMLHttpRequest();
        let formData = new FormData();

        xhr.withCredentials = true;

        this.$emit("before-upload", {
          xhr: xhr,
          formData: formData,
        });

        for (let file of this.files) {
          formData.append(this.name, file, file.name);
        }

        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            this.progress = Math.round((event.loaded * 100) / event.total);
          }

          this.$emit("progress", {
            originalEvent: event,
            progress: this.progress,
          });
        });

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            this.progress = 0;

            if (xhr.status >= 200 && xhr.status < 300) {
              if (this.fileLimit) {
                this.uploadedFileCount += this.files.length;
              }

              this.$emit("upload", {
                xhr: xhr,
                files: this.files,
              });
            } else {
              this.$emit("error", {
                xhr: xhr,
                files: this.files,
              });
            }

            this.clear();
          }
        };

        xhr.open("POST", url, true);
        xhr.setRequestHeader("Authorization", `Bearer ${store.getters.getAuthToken}`);

        console.log(store.getters.getAuthToken, "store.getters.getAuthToken");

        // xhr.withCredentials = true;

        this.$emit("before-send", {
          xhr: xhr,
          formData: formData,
        });

        xhr.withCredentials = this.withCredentials;

        xhr.send(formData);
      }
    },
    clear() {
      this.files = [];
      this.messages = null;
      this.$emit("clear");

      if (this.isAdvanced) {
        this.clearInputElement();
      }
    },
    onFocus() {
      this.focused = true;
    },
    onBlur() {
      this.focused = false;
    },
    isFileSelected(file) {
      if (this.files && this.files.length) {
        for (let sFile of this.files) {
          if (sFile.name + sFile.type + sFile.size === file.name + file.type + file.size)
            return true;
        }
      }

      return false;
    },
    isIE11() {
      return !!window["MSInputMethodContext"] && !!document["documentMode"];
    },
    validate(file) {
      if (this.maxFileSize && file.size > this.maxFileSize) {
        this.messages.push(
          this.invalidFileSizeMessage
            .replace("{0}", file.name)
            .replace("{1}", this.formatSize(this.maxFileSize))
        );
        return false;
      }

      return true;
    },
    onDragEnter(event) {
      if (!this.disabled) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    onDragOver() {
      if (!this.disabled) {
        DomHandler.addClass(this.$refs.content, "p-fileupload-highlight");
        event.stopPropagation();
        event.preventDefault();
      }
    },
    onDragLeave() {
      if (!this.disabled) {
        DomHandler.removeClass(this.$refs.content, "p-fileupload-highlight");
      }
    },
    onDrop() {
      if (!this.disabled) {
        DomHandler.removeClass(this.$refs.content, "p-fileupload-highlight");
        event.stopPropagation();
        event.preventDefault();

        const files = event.dataTransfer ? event.dataTransfer.files : event.target.files;
        const allowDrop = this.multiple || (files && files.length === 1);

        if (allowDrop) {
          this.onFileSelect(event);
        }
      }
    },
    onBasicUploaderClick() {
      if (this.hasFiles) this.upload();
      else this.$refs.fileInput.click();
    },
    remove(index) {
      this.clearInputElement();
      this.files.splice(index, 1);
      this.files = [...this.files];
    },
    isImage(file) {
      return /^image\//.test(file.type);
    },
    clearInputElement() {
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    clearIEInput() {
      if (this.$refs.fileInput) {
        this.duplicateIEEvent = true; //IE11 fix to prevent onFileChange trigger again
        this.$refs.fileInput.value = "";
      }
    },
    formatSize(bytes) {
      if (bytes === 0) {
        return "0 B";
      }
      let k = 1000,
        dm = 3,
        sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
        i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    },
    isFileLimitExceeded() {
      if (
        this.fileLimit &&
        this.fileLimit <= this.files.length + this.uploadedFileCount &&
        this.focused
      ) {
        this.focused = false;
      }

      return this.fileLimit && this.fileLimit < this.files.length + this.uploadedFileCount;
    },
    checkFileLimit() {
      if (this.isFileLimitExceeded()) {
        this.messages.push(this.invalidFileLimitMessage.replace("{0}", this.fileLimit.toString()));
      }
    },
  },
  computed: {
    isAdvanced() {
      return this.mode === "advanced";
    },
    isBasic() {
      return this.mode === "basic";
    },
    advancedChooseButtonClass() {
      return [
        "p-button p-component p-fileupload-choose",
        {
          "p-disabled": this.disabled,
          "p-focus": this.focused,
        },
      ];
    },
    basicChooseButtonClass() {
      return [
        "p-button p-component p-fileupload-choose",
        {
          "p-fileupload-choose-selected": this.hasFiles,
          "p-disabled": this.disabled,
          "p-focus": this.focused,
        },
      ];
    },
    basicChooseButtonIconClass() {
      return [
        "p-button-icon p-button-icon-left pi",
        {
          "pi-plus": !this.hasFiles || this.auto,
          "pi-upload": this.hasFiles && !this.auto,
        },
      ];
    },
    basicChooseButtonLabel() {
      return this.auto
        ? this.chooseButtonLabel
        : this.hasFiles
        ? this.files[0].name
        : this.chooseButtonLabel;
    },
    hasFiles() {
      return this.files && this.files.length > 0;
    },
    chooseDisabled() {
      return (
        this.disabled ||
        (this.fileLimit && this.fileLimit <= this.files.length + this.uploadedFileCount)
      );
    },
    uploadDisabled() {
      return this.disabled || !this.hasFiles;
    },
    cancelDisabled() {
      return this.disabled || !this.hasFiles;
    },
    chooseButtonLabel() {
      return this.chooseLabel || this.$primevue.config.locale.choose;
    },
    uploadButtonLabel() {
      return this.uploadLabel || this.$primevue.config.locale.upload;
    },
    cancelButtonLabel() {
      return this.cancelLabel || this.$primevue.config.locale.cancel;
    },
  },
  components: {
    FileUploadButton: Button,
    FileUploadProgressBar: ProgressBar,
    FileUploadMessage: Message,
  },
  directives: {
    ripple: Ripple,
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/_mixin.scss";
@import "@/styles/_variables.scss";
.p-fileupload-content {
  position: relative;
}
:deep(.p-fileupload-content) {
  padding: 1rem;
  border: solid 2px transparent !important;
  min-height: 5rem;
  .drop-message {
    color: $BrinkPink;
    display: none;
  }
}

:deep(.p-fileupload-highlight) {
  border: #ff767e solid 2px !important;

  .drop-message {
    display: block;
  }
}

:deep(.p-fileupload-empty) {
  padding: 1rem;
}

.p-fileupload-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-radius: 6px;
  position: relative;
  &:not(:last-child) {
    border-bottom: 1px solid $Grey1;
  }
}

.p-fileupload-row > div {
  padding: 0;
}
.p-fileupload-row > div:first-child {
  width: 40px;
  height: 40px;
  margin-right: 10px;
  img {
    max-width: 32px;
    height: auto;
  }
}
.p-fileupload-row > div.file-remove {
  position: absolute;
  right: 0;
  button {
    padding: 0;
    width: 30px;
    height: 30px;
    background: #ffffff;
    border: 1px solid $Grey1;
    border-radius: 6px;
    color: $Grey3;
    span {
      padding-top: 2px;
    }
  }
}

.p-fileupload-row > div:last-child {
  text-align: right;
}

.p-fileupload-content .p-progressbar {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.p-button.p-fileupload-choose {
  position: relative;
  overflow: hidden;
}

.p-button.p-fileupload-choose input[type="file"] {
  display: none;
}

.p-fileupload-choose.p-fileupload-choose-selected input[type="file"] {
  display: none;
}

.p-fluid .p-fileupload .p-button {
  width: auto;
}

.progress {
  margin: 12px 0;
}
img[role="presentation"] {
  max-height: 40px;
}
.file-info__name {
  text-overflow: ellipsis;
  overflow: hidden;
  //width: calc(100vw - 350px);
  white-space: nowrap;
  display: block;
}
@include mobile {
  .file-info__name {
    width: calc(100vw - 180px);
  }
  .file-size {
    font-size: 0.8rem;
  }
}
</style>
